import React from "react";
import { Typography, Box, TextField, IconButton } from "@material-ui/core";
import { Edit as EditIcon } from "@material-ui/icons";

export type Segment =
  | { type: "text"; value: string }
  | { type: "question"; value: string }
  | { type: "option"; value: string }
  | { type: "patient"; value: string }
  | { type: "linebreak" };

const patientVariables: Record<string, string> = {
  patient_joint: "Patient joint",
  patient_ailment: "Patient ailment",
  patient_market: "Patient market",
  patient_locale: "Patient locale",
  patient_premium: "Patient premium",
  patient_medications: "Patient medications",
  patient_subscriptions: "Patient subscriptions",
};

type ReplacementKind = "question" | "option" | "patient";
type Replacement = { text: string; kind: ReplacementKind };

const PLACEHOLDER = "\uFFFF";

const splitOnConjunctions = (seg: Segment): Segment[] => {
  if (seg.type !== "text") return [seg];
  return seg.value.split(/( and | or )/).flatMap((part, i): Segment[] => {
    if (i % 2 === 1) return [{ type: "linebreak" }, { type: "text", value: part.trim() + " " }];
    return part ? [{ type: "text", value: part }] : [];
  });
};

export const resolveFeelSegments = (
  predicate: string,
  questionFeelIdToText: Record<string, string>,
  optionFeelIdToText: Record<string, string>
): Segment[] => {
  const allMappings: { key: string; text: string; kind: ReplacementKind }[] = [
    ...Object.entries(questionFeelIdToText).map(([key, text]) => ({ key, text, kind: "question" as const })),
    ...Object.entries(optionFeelIdToText).map(([key, text]) => ({ key, text, kind: "option" as const })),
    ...Object.entries(patientVariables).map(([key, text]) => ({ key, text, kind: "patient" as const })),
  ].sort((a, b) => b.key.length - a.key.length);

  const replacements: Replacement[] = allMappings.map(({ text, kind }) => ({ text, kind }));
  const working = allMappings.reduce(
    (acc, { key }, idx) => acc.split(key).join(PLACEHOLDER + idx + PLACEHOLDER),
    predicate
  );

  const segments: Segment[] = working.split(PLACEHOLDER).flatMap((part, i): Segment[] => {
    if (i % 2 === 0) return part ? [{ type: "text", value: part }] : [];
    const r = replacements[Number(part)];
    return [{ type: r.kind, value: r.text }];
  });

  return segments.flatMap(splitOnConjunctions);
};

export const PredicateField: React.VFC<{
  predicate: string;
  feelIdToText: Record<string, string>;
  optionFeelIdToText?: Record<string, string>;
  isEditing: boolean;
  onStartEditing: () => void;
  onStopEditing: () => void;
  onChange: (value: string) => void;
  error?: string;
  onAiRequest?: () => void;
}> = ({
  predicate,
  feelIdToText,
  optionFeelIdToText = {},
  isEditing,
  onStartEditing,
  onStopEditing,
  onChange,
  error,
  onAiRequest,
}) => {
  if (isEditing) {
    return (
      <Box>
        <TextField
          size="small"
          fullWidth
          autoFocus
          placeholder="FEEL predicate"
          value={predicate}
          onChange={e => onChange(e.target.value)}
          onBlur={onStopEditing}
          variant="outlined"
          error={!!error}
        />
        {error && (
          <Typography variant="caption" style={{ color: "#f44336", marginTop: 2, display: "block" }}>
            {error}
          </Typography>
        )}
      </Box>
    );
  }

  const segments = resolveFeelSegments(predicate, feelIdToText, optionFeelIdToText);

  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        style={{
          padding: "8px 12px",
          background: error ? "#fff0f0" : "#f5f5f5",
          borderRadius: 4,
          gap: 8,
          border: error ? "1px solid #f44336" : undefined,
        }}
      >
        <Typography
          variant="body2"
          onClick={onStartEditing}
          style={{ fontSize: 13, wordBreak: "break-word", flex: 1, cursor: "pointer" }}
        >
          {segments.length === 0 ? (
            <em>click to edit</em>
          ) : (
            segments.map((s, i) =>
              s.type === "question" ? (
                <code key={i} style={{ background: "#e0e0e0", borderRadius: 3, padding: "1px 4px" }}>
                  {s.value}
                </code>
              ) : s.type === "option" ? (
                <code key={i} style={{ background: "#d4edda", borderRadius: 3, padding: "1px 4px" }}>
                  "{s.value}"
                </code>
              ) : s.type === "patient" ? (
                <code key={i} style={{ background: "#d4e4f7", borderRadius: 3, padding: "1px 4px" }}>
                  {s.value}
                </code>
              ) : s.type === "linebreak" ? (
                <br key={i} />
              ) : (
                <span key={i}>{s.value}</span>
              )
            )
          )}
        </Typography>
        <IconButton size="small" onClick={onStartEditing} style={{ padding: 4, flexShrink: 0 }}>
          <EditIcon style={{ fontSize: 16, color: "#888" }} />
        </IconButton>
        {onAiRequest && (
          <IconButton size="small" onClick={onAiRequest} style={{ padding: 4, flexShrink: 0 }}>
            <span style={{ fontSize: 16 }}>🤖</span>
          </IconButton>
        )}
      </Box>
      {error && (
        <Typography variant="caption" style={{ color: "#f44336", marginTop: 2, display: "block" }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};
